"use client";

import { useCallback, useEffect } from "react";
import { useTranslations } from 'use-intl';
import AlertPortal from "./portal";
import View from "./View";
import useAlert from "@/shared/hooks/useAlert";
import { signOutWithCleanup } from "@/entities/auth/api/clientAuth";

interface ApiErrorDetail {
  result_code?: string;
  message?: string;
  msg?: string;
  shouldRetry?: boolean;
  shouldLogout?: boolean;
  shouldRedirect?: boolean;
  redirectUrl?: string;
}

export const Alert = () => {
  const { alert, addAlert } = useAlert();
  const t = useTranslations();
  // const { logOut } = useAuth();

  const handleApiError = useCallback(
    (event: CustomEvent<ApiErrorDetail>) => {
      if (!event.detail) return;
      const { result_code, message, msg, shouldLogout, shouldRedirect, redirectUrl } = event.detail;

      // 리다이렉트가 필요한 에러인 경우 (로컬 환경 제외)
      if (shouldRedirect && redirectUrl && process.env.NODE_ENV !== 'development') {
        window.location.href = redirectUrl;
        return;
      }

      // 로그아웃이 필요한 에러인 경우
      if (shouldLogout) {
        addAlert({
          message: message || msg || '로그인 정보가 만료되었습니다. 다시 로그인해주세요.',
          onConfirm: () => {
            // console.log("로그아웃이 필요한 에러가 발생했습니다:", message || msg);
            signOutWithCleanup();
          }
        });
        return;
      }

      // result_code가 있는 경우 (API 응답 에러)
      if (result_code && result_code !== '100') {
        // result_code를 그대로 사용해서 다국어 메시지 가져오기
        let translatedMessage;
        try {
          const translated = t(result_code);
          // 번역된 메시지가 원본 키와 같다면 키가 존재하지 않는 것
          translatedMessage = translated !== result_code ? translated : null;
        } catch (error) {
          // 해당 result_code가 다국어 메시지에 없으면 null로 설정
          translatedMessage = null;
        }

        addAlert({
          message: translatedMessage || message || msg || 'API 요청이 실패했습니다.'
        });
        return;
      }

      // 기존 네트워크 에러 처리
      addAlert({ message: message || msg || 'API 요청이 실패했습니다.' });

    },
    [addAlert, t]
  );

  useEffect(() => {
    // apiError 이벤트 처리
    window.addEventListener("apiError", handleApiError as () => void);
    return (): void => {
      window.removeEventListener("apiError", handleApiError as () => void);
    };
  }, [handleApiError]);

  if (!alert?.message) return null;

  return (
    <AlertPortal>
      <View alert={alert} />
    </AlertPortal>
  );
};
